import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCard, RefreshCw, AlertTriangle, Edit, Download, Trash2, FileText, Eye,
  DollarSign, TrendingUp, Clock, XCircle, CheckCircle, Car, Home, Package, Shirt, Truck, ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { NoteIconButton } from './NotesModal';
import PDFPreviewModal from './PDFPreviewModal';
import { paymentsAPI } from '../../services/api';
import api from '../../services/api';

const SERVICE_ICONS = {
  rides: Car,
  rides: Car,
  laundry: Shirt,
  courier: Truck,
  errands: Package,
};

const PaymentsTab = ({
  paymentsData,
  paymentsLoading,
  paymentsFilter,
  setPaymentsFilter,
  handleDownloadInvoice,
  handleDownloadReceipt,
  handleDeletePayment,
  handleUpdatePayment,
  noteCounts,
  setNotesModal
}) => {
  const [showPaymentEditModal, setShowPaymentEditModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentEditData, setPaymentEditData] = useState({
    cash_amount: '',
    payment_method: '',
    notes: ''
  });
  const [pdfPreview, setPdfPreview] = useState({ isOpen: false, blob: null, isLoading: false, error: null, title: '', filename: '' });
  const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard' or 'list'
  const [revenueData, setRevenueData] = useState({
    totalPaid: 0,
    totalPending: 0,
    totalFailed: 0,
    todayRevenue: 0,
    weekRevenue: 0,
    monthRevenue: 0,
    serviceBreakdown: {}
  });

  // Calculate revenue metrics from payments data
  useEffect(() => {
    if (paymentsData?.payments) {
      const payments = paymentsData.payments;
      const now = new Date();
      const today = now.toISOString().slice(0, 10);
      const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      
      let totalPaid = 0;
      let totalPending = 0;
      let totalFailed = 0;
      let todayRevenue = 0;
      let weekRevenue = 0;
      let monthRevenue = 0;
      const serviceBreakdown = {};

      payments.forEach(payment => {
        const amount = payment.total_amount || 0;
        const paidAmount = payment.total_paid || 0;
        const serviceType = payment.service_type || 'other';

        // Service breakdown
        if (!serviceBreakdown[serviceType]) {
          serviceBreakdown[serviceType] = { paid: 0, pending: 0, count: 0 };
        }
        serviceBreakdown[serviceType].count++;

        if (payment.payment_status === 'paid') {
          totalPaid += paidAmount;
          serviceBreakdown[serviceType].paid += paidAmount;
          
          // Time-based revenue
          const paidDate = payment.paid_at || payment.created_at;
          if (paidDate) {
            const dateStr = paidDate.slice(0, 10);
            if (dateStr === today) todayRevenue += paidAmount;
            if (dateStr >= weekAgo) weekRevenue += paidAmount;
            if (dateStr >= monthAgo) monthRevenue += paidAmount;
          }
        } else if (payment.payment_status === 'pending') {
          totalPending += amount;
          serviceBreakdown[serviceType].pending += amount;
        } else if (payment.payment_status === 'failed') {
          totalFailed += amount;
        }
      });

      setRevenueData({
        totalPaid,
        totalPending,
        totalFailed,
        todayRevenue,
        weekRevenue,
        monthRevenue,
        serviceBreakdown
      });
    }
  }, [paymentsData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Preview Invoice as PDF
  const handlePreviewInvoice = async (payment) => {
    setPdfPreview({ 
      isOpen: true, 
      blob: null, 
      isLoading: true, 
      error: null, 
      title: `Invoice - ${payment.service_name || 'Service'}`,
      filename: `invoice_${payment.id.slice(0, 8).toUpperCase()}.pdf`
    });
    
    try {
      const response = await paymentsAPI.getInvoice(payment.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      setPdfPreview(prev => ({ ...prev, blob, isLoading: false }));
    } catch (error) {
      console.error('Failed to load invoice preview:', error);
      setPdfPreview(prev => ({ ...prev, isLoading: false, error: 'Failed to load invoice' }));
    }
  };

  // Preview Receipt as PDF
  const handlePreviewReceipt = async (payment) => {
    setPdfPreview({ 
      isOpen: true, 
      blob: null, 
      isLoading: true, 
      error: null, 
      title: `Receipt - ${payment.service_name || 'Service'}`,
      filename: `receipt_${payment.id.slice(0, 8).toUpperCase()}.pdf`
    });
    
    try {
      const response = await paymentsAPI.getReceipt(payment.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      setPdfPreview(prev => ({ ...prev, blob, isLoading: false }));
    } catch (error) {
      console.error('Failed to load receipt preview:', error);
      const errorMsg = error.response?.status === 400 ? 'No payments found for this booking' : 'Failed to load receipt';
      setPdfPreview(prev => ({ ...prev, isLoading: false, error: errorMsg }));
    }
  };

  const closePdfPreview = () => {
    setPdfPreview({ isOpen: false, blob: null, isLoading: false, error: null, title: '', filename: '' });
  };

  const onUpdatePayment = async () => {
    await handleUpdatePayment(selectedPayment.id, paymentEditData);
    setShowPaymentEditModal(false);
    setSelectedPayment(null);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Payment Management</h1>
        
        {/* View Toggle */}
        <div className="flex gap-2">
          <div className="flex bg-[var(--background-subtle)] rounded-lg p-1">
            <button
              onClick={() => setViewMode('dashboard')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'dashboard' ? 'bg-[var(--background-paper)] shadow text-[var(--primary)]' : 'text-[var(--text-muted)]'
              }`}
              data-testid="view-dashboard"
            >
              <DollarSign className="w-4 h-4 inline mr-1" />
              Dashboard
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list' ? 'bg-[var(--background-paper)] shadow text-[var(--primary)]' : 'text-[var(--text-muted)]'
              }`}
              data-testid="view-list"
            >
              <CreditCard className="w-4 h-4 inline mr-1" />
              Transactions
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'dashboard' ? (
        /* REVENUE DASHBOARD VIEW */
        <div className="space-y-6">
          {/* Main Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Paid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card p-4 lg:p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200"
              data-testid="total-paid-card"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
                </div>
                <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">
                  Collected
                </span>
              </div>
              <p className="text-2xl lg:text-3xl font-bold text-green-700">{formatCurrency(revenueData.totalPaid)}</p>
              <p className="text-sm text-green-600 mt-1">Total Paid</p>
            </motion.div>

            {/* Pending */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card p-4 lg:p-6 bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200"
              data-testid="pending-payments-card"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-5 h-5 lg:w-6 lg:h-6 text-amber-600" />
                </div>
                <span className="text-xs font-medium text-amber-600 bg-amber-100 px-2 py-1 rounded">
                  Awaiting
                </span>
              </div>
              <p className="text-2xl lg:text-3xl font-bold text-amber-700">{formatCurrency(revenueData.totalPending)}</p>
              <p className="text-sm text-amber-600 mt-1">Pending Payment</p>
            </motion.div>

            {/* Failed */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card p-4 lg:p-6 bg-gradient-to-br from-red-50 to-rose-50 border-red-200"
              data-testid="failed-payments-card"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="w-5 h-5 lg:w-6 lg:h-6 text-red-600" />
                </div>
                <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded">
                  Failed
                </span>
              </div>
              <p className="text-2xl lg:text-3xl font-bold text-red-700">{formatCurrency(revenueData.totalFailed)}</p>
              <p className="text-sm text-red-600 mt-1">Failed Payments</p>
            </motion.div>

            {/* This Month */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card p-4 lg:p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200"
              data-testid="month-revenue-card"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
                </div>
                <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">
                  30 Days
                </span>
              </div>
              <p className="text-2xl lg:text-3xl font-bold text-blue-700">{formatCurrency(revenueData.monthRevenue)}</p>
              <p className="text-sm text-blue-600 mt-1">This Month</p>
            </motion.div>
          </div>

          {/* Revenue Time Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Time-based Revenue */}
            <div className="card p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[var(--primary)]" />
                Revenue Timeline
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-[var(--background-subtle)] rounded-lg">
                  <span className="text-[var(--text-muted)]">Today</span>
                  <span className="font-semibold text-green-600">{formatCurrency(revenueData.todayRevenue)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[var(--background-subtle)] rounded-lg">
                  <span className="text-[var(--text-muted)]">Last 7 Days</span>
                  <span className="font-semibold text-green-600">{formatCurrency(revenueData.weekRevenue)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[var(--background-subtle)] rounded-lg">
                  <span className="text-[var(--text-muted)]">Last 30 Days</span>
                  <span className="font-semibold text-green-600">{formatCurrency(revenueData.monthRevenue)}</span>
                </div>
              </div>
            </div>

            {/* Service Breakdown */}
            <div className="card p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[var(--primary)]" />
                By Service Type
              </h3>
              <div className="space-y-3">
                {Object.entries(revenueData.serviceBreakdown).map(([type, data]) => {
                  const Icon = SERVICE_ICONS[type] || Package;
                  return (
                    <div key={type} className="flex items-center justify-between p-3 bg-[var(--background-subtle)] rounded-lg">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-[var(--text-muted)]" />
                        <span className="capitalize text-[var(--text-muted)]">{type}</span>
                        <span className="text-xs bg-[var(--primary)]/10 text-[var(--primary)] px-1.5 py-0.5 rounded">
                          {data.count}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">{formatCurrency(data.paid)}</p>
                        {data.pending > 0 && (
                          <p className="text-xs text-amber-600">{formatCurrency(data.pending)} pending</p>
                        )}
                      </div>
                    </div>
                  );
                })}
                {Object.keys(revenueData.serviceBreakdown).length === 0 && (
                  <p className="text-center text-[var(--text-muted)] py-4">No service data available</p>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <span className="text-[var(--text-muted)]">View all payment transactions</span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setViewMode('list')}
              >
                View Transactions
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        /* TRANSACTIONS LIST VIEW */
        <>
          {/* Filter Buttons */}
          <div className="flex justify-end mb-4">
            <div className="flex bg-[var(--background-subtle)] rounded-lg p-1">
              {[
                { id: 'all', label: 'All', count: (paymentsData.summary?.pending || 0) + (paymentsData.summary?.deposit_paid || 0) + (paymentsData.summary?.paid || 0) },
                { id: 'pending', label: 'Pending', count: paymentsData.summary?.pending || 0 },
                { id: 'deposit_paid', label: 'Deposit Paid', count: paymentsData.summary?.deposit_paid || 0 },
                { id: 'paid', label: 'Fully Paid', count: paymentsData.summary?.paid || 0 }
              ].map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setPaymentsFilter(filter.id)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                    paymentsFilter === filter.id ? 'bg-[var(--background-paper)] shadow text-[var(--primary)]' : 'text-[var(--text-muted)]'
                  }`}
                  data-testid={`payment-filter-${filter.id}`}
                >
                  {filter.label}
                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                    paymentsFilter === filter.id ? 'bg-[var(--primary)] text-white' : 'bg-[var(--background-subtle)]'
                  }`}>
                    {filter.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-[var(--warning)]">{paymentsData.summary?.pending || 0}</p>
          <p className="text-sm text-[var(--text-muted)]">Pending Payment</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-[#D4AF37]">{paymentsData.summary?.deposit_paid || 0}</p>
          <p className="text-sm text-[var(--text-muted)]">Deposit Paid</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-[var(--success)]">{paymentsData.summary?.paid || 0}</p>
          <p className="text-sm text-[var(--text-muted)]">Fully Paid</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-[var(--primary)]">{paymentsData.total || 0}</p>
          <p className="text-sm text-[var(--text-muted)]">Total Records</p>
        </div>
      </div>

      {/* Payments List */}
      <div className="card overflow-hidden">
        <div className="p-4 bg-gradient-to-r from-green-600 to-green-700 text-white">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payments ({paymentsData.payments?.length || 0})
          </h2>
        </div>

        {paymentsLoading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[var(--success)]" />
            <p className="mt-2 text-[var(--text-muted)]">Loading payments...</p>
          </div>
        ) : paymentsData.payments?.length === 0 ? (
          <div className="p-8 text-center text-[var(--text-muted)]">
            <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No payments found</p>
          </div>
        ) : (
          <div className="divide-y">
            {paymentsData.payments?.map(payment => (
              <div key={payment.id} className={`p-4 hover:bg-[var(--background-subtle)] ${payment.is_urgent ? 'bg-[var(--warning)]/10 border-l-4 border-amber-500' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-semibold text-lg">{payment.service_name}</span>
                      {payment.is_urgent && (
                        <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          URGENT
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        payment.payment_status === 'paid' ? 'bg-[var(--success)]/15 text-green-700' :
                        payment.payment_status === 'deposit_paid' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' :
                        'bg-[var(--warning)]/15 text-yellow-700'
                      }`}>
                        {payment.payment_status === 'paid' ? 'Fully Paid' : 
                         payment.payment_status === 'deposit_paid' ? 'Deposit Paid' : 'Pending'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-[var(--text-muted)]">
                      <div>
                        <span className="font-medium text-[var(--text-primary)]">Customer:</span>
                        <p>{payment.customer_name || payment.customer_email}</p>
                      </div>
                      <div>
                        <span className="font-medium text-[var(--text-primary)]">Date:</span>
                        <p>{payment.scheduled_date} at {payment.scheduled_time}</p>
                      </div>
                      <div>
                        <span className="font-medium text-[var(--text-primary)]">Total Amount:</span>
                        <p className="text-lg font-bold text-[var(--primary)]">${payment.total_amount?.toFixed(2)}</p>
                      </div>
                      <div>
                        <span className="font-medium text-[var(--text-primary)]">Paid / Remaining:</span>
                        <p>
                          <span className="text-[var(--success)]">${payment.total_paid?.toFixed(2) || '0.00'}</span>
                          {payment.remaining_amount > 0 && (
                            <span className="text-[var(--error)] ml-2">/ ${payment.remaining_amount?.toFixed(2)} due</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Urgent Fee Info */}
                    {payment.urgent_fee_amount > 0 && (
                      <p className="mt-2 text-sm text-amber-600">
                        Includes urgent fee: +${payment.urgent_fee_amount?.toFixed(2)} (25%)
                      </p>
                    )}

                    {/* Payment Notes */}
                    {payment.payment_notes && (
                      <p className="mt-2 text-sm bg-[var(--background-subtle)] p-2 rounded">
                        <span className="font-medium">Notes:</span> {payment.payment_notes}
                      </p>
                    )}

                    {/* Transaction History */}
                    {payment.transactions?.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-sm text-[#D4AF37] cursor-pointer hover:underline">
                          View {payment.transactions.length} transaction{payment.transactions.length !== 1 ? 's' : ''}
                        </summary>
                        <div className="mt-2 space-y-1 pl-2 border-l-2 border-[var(--border-primary)]">
                          {payment.transactions.map(txn => (
                            <div key={txn.id} className="text-sm text-[var(--text-muted)] flex items-center gap-2">
                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                txn.payment_type === 'cash' ? 'bg-[var(--success)]/15 text-green-700' : 'bg-[#D4AF37]/10 text-[#D4AF37]'
                              }`}>
                                {txn.payment_type || 'stripe'}
                              </span>
                              ${txn.amount?.toFixed(2)} - {txn.created_at?.slice(0, 10)}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 ml-4">
                    {/* Notes */}
                    <NoteIconButton
                      entityType="booking"
                      entityId={payment.id}
                      entityName={`${payment.service_name} - ${payment.customer_name || payment.customer_email}`}
                      noteCount={noteCounts[`booking_${payment.id}`] || 0}
                      onOpen={(type, id, name) => setNotesModal({ open: true, entityType: type, entityId: id, entityName: name })}
                    />
                    
                    {/* Edit Payment */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedPayment(payment);
                        setPaymentEditData({
                          cash_amount: '',
                          payment_method: payment.payment_method || '',
                          notes: payment.payment_notes || ''
                        });
                        setShowPaymentEditModal(true);
                      }}
                      className="text-[#D4AF37] border-blue-300 hover:bg-[#D4AF37]/10"
                      data-testid={`edit-payment-${payment.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    
                    {/* Preview Invoice */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePreviewInvoice(payment)}
                      className="text-blue-500 border-blue-300 hover:bg-blue-500/10"
                      data-testid={`preview-invoice-${payment.id}`}
                      title="Preview Invoice"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    
                    {/* Download Invoice */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadInvoice(payment.id)}
                      className="text-[var(--success)] border-green-300 hover:bg-[var(--success)]/10"
                      data-testid={`invoice-${payment.id}`}
                      title="Download Invoice"
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                    
                    {/* Receipt Preview & Download - only show if there are actual transactions */}
                    {(payment.payment_status === 'paid' || payment.payment_status === 'deposit_paid') && 
                     payment.transactions?.length > 0 && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePreviewReceipt(payment)}
                          className="text-purple-500 border-purple-300 hover:bg-purple-500/10"
                          data-testid={`preview-receipt-${payment.id}`}
                          title="Preview Receipt"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadReceipt(payment.id)}
                          className="text-[var(--primary)] border-[var(--primary)]/30 hover:bg-[var(--primary)]/10"
                          data-testid={`receipt-${payment.id}`}
                          title="Download Receipt"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    
                    {/* Delete */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeletePayment(payment.id, payment.service_name)}
                      className="text-[var(--error)] border-red-300 hover:bg-[var(--error)]/10"
                      data-testid={`delete-payment-${payment.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
        </>
      )}

      {/* Payment Edit Modal */}
      {showPaymentEditModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--background-paper)] rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-4 border-b bg-gradient-to-r from-green-600 to-green-700 rounded-t-xl text-white">
              <h3 className="font-semibold flex items-center gap-2">
                <Edit className="w-5 h-5" />
                Edit Payment - {selectedPayment.service_name}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-[var(--text-muted)] mb-2">
                  Total: ${selectedPayment.total_amount?.toFixed(2)} | 
                  Paid: ${selectedPayment.total_paid?.toFixed(2) || '0.00'} | 
                  Remaining: ${selectedPayment.remaining_amount?.toFixed(2)}
                </p>
              </div>
              
              <div>
                <Label htmlFor="cash_amount">Record Cash Payment ($)</Label>
                <Input
                  id="cash_amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Enter cash amount received"
                  value={paymentEditData.cash_amount}
                  onChange={(e) => setPaymentEditData({ ...paymentEditData, cash_amount: e.target.value })}
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  Enter amount if customer paid in cash
                </p>
              </div>
              
              <div>
                <Label htmlFor="payment_method">Payment Method</Label>
                <Select 
                  value={paymentEditData.payment_method} 
                  onValueChange={(v) => setPaymentEditData({ ...paymentEditData, payment_method: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stripe">Stripe</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="payment_notes">Notes</Label>
                <Textarea
                  id="payment_notes"
                  placeholder="Add payment notes..."
                  value={paymentEditData.notes}
                  onChange={(e) => setPaymentEditData({ ...paymentEditData, notes: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowPaymentEditModal(false);
                setSelectedPayment(null);
              }}>
                Cancel
              </Button>
              <Button onClick={onUpdatePayment} className="bg-[var(--success)] hover:bg-[var(--success)]/80 text-white">
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={pdfPreview.isOpen}
        onClose={closePdfPreview}
        pdfBlob={pdfPreview.blob}
        title={pdfPreview.title}
        filename={pdfPreview.filename}
        isLoading={pdfPreview.isLoading}
        error={pdfPreview.error}
      />
    </motion.div>
  );
};

export default PaymentsTab;
